import { pb } from '../helpers/pocketbase_helper';
import { createModuleLogger } from '../utils/logger';

// Crear logger para este servicio
const serviceLogger = createModuleLogger('TipoTrabajoService');

/**
 * Valida que el trabajo interno sea un valor booleano válido
 * @param {any} trabajoInterno - Valor a validar
 * @returns {boolean} - true si es válido, false si no
 */
export const validateTrabajoInterno = (trabajoInterno) => {
  return typeof trabajoInterno === 'boolean';
};

/**
 * Actualiza el trabajo interno de una orden en PocketBase
 * @param {string} ordenId - ID de la orden a actualizar
 * @param {boolean} trabajoInterno - Nuevo trabajo interno (true = INTERNO, false = EXTERNO)
 * @returns {Promise<{success: boolean, data?: any, error?: string}>} - Resultado de la operación
 */
export const updateTrabajoInterno = async (ordenId, trabajoInterno) => {
  try {
    // Validar parámetros de entrada
    if (!ordenId || ordenId.trim() === '') {
      serviceLogger.error('ID de orden es requerido', { ordenId });
      return {
        success: false,
        error: 'ID de orden es requerido'
      };
    }

    if (!validateTrabajoInterno(trabajoInterno)) {
      serviceLogger.error('Trabajo interno inválido', { trabajoInterno });
      return {
        success: false,
        error: 'Trabajo interno debe ser un valor booleano válido'
      };
    }

    serviceLogger.info('Iniciando actualización de trabajo interno', { 
      ordenId, 
      trabajoInterno,
      tipoTexto: trabajoInterno ? 'INTERNO' : 'EXTERNO'
    });

    // Actualizar la orden en PocketBase
    const updatedOrden = await pb.collection('orden_produccion').update(ordenId, {
      trabajo_interno: trabajoInterno
    });

    serviceLogger.info('Trabajo interno actualizado exitosamente', { 
      ordenId, 
      trabajoInterno,
      updatedAt: updatedOrden.updated
    });

    return {
      success: true,
      data: updatedOrden
    };

  } catch (error) {
    const errorMessage = `Error al actualizar el trabajo interno: ${error.message}`;
    serviceLogger.error('Error en actualización de trabajo interno', { 
      error: error.message, 
      ordenId, 
      trabajoInterno 
    });

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Obtiene el trabajo interno actual de una orden
 * @param {string} ordenId - ID de la orden
 * @returns {Promise<{success: boolean, data?: boolean, error?: string}>} - Trabajo interno actual
 */
export const getTrabajoInterno = async (ordenId) => {
  try {
    if (!ordenId || ordenId.trim() === '') {
      serviceLogger.error('ID de orden es requerido para obtener trabajo interno', { ordenId });
      return {
        success: false,
        error: 'ID de orden es requerido'
      };
    }

    serviceLogger.debug('Obteniendo trabajo interno actual', { ordenId });

    const orden = await pb.collection('orden_produccion').getOne(ordenId, {
      fields: 'id,trabajo_interno'
    });

    serviceLogger.debug('Trabajo interno obtenido', { 
      ordenId, 
      trabajoInterno: orden.trabajo_interno 
    });

    return {
      success: true,
      data: orden.trabajo_interno
    };

  } catch (error) {
    const errorMessage = `Error al obtener el trabajo interno: ${error.message}`;
    serviceLogger.error('Error al obtener trabajo interno', { 
      error: error.message, 
      ordenId 
    });

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Valida y formatea el trabajo interno para mostrar en la UI
 * @param {boolean} trabajoInterno - Valor booleano del trabajo interno
 * @returns {Object} - Objeto con información formateada
 */
export const formatTrabajoInterno = (trabajoInterno) => {
  if (!validateTrabajoInterno(trabajoInterno)) {
    return {
      value: null,
      label: 'No definido',
      badgeClass: 'bg-secondary',
      isValid: false
    };
  }

  return {
    value: trabajoInterno,
    label: trabajoInterno ? 'INTERNO' : 'EXTERNO',
    badgeClass: trabajoInterno ? 'bg-success' : 'bg-info',
    isValid: true
  };
};
